import { Box, Typography } from '@mui/material';
import { memo, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import Table from '../../../../components/Table/Table';
import Button from '../../../../components/Button/Button';

import { Sort } from '../../../../models/CommonModels';
import { Device } from '../../../../models/UserDevicesModels';
import UserDetailsContext from '../../../../contexts/UserDetailsContext/UserDetailsContext';
import { RootState } from '../../../../stores/Store';
import { UserOperationsType } from '../../../../models/UsersModels';

import useUserDetailsDevicesTable from './useUserDetailsDevicesTable';
import usePortalConfig from '../../../../hooks/usePortalConfig/usePortalConfig';
import useRolePermissions from '../../../../hooks/useRolePermissions/useRolePermissions';

interface UserDetailsDevicesTableProps {
  devices: Device[];
  isLoading: boolean;
  isPerformingAction: boolean;
  resendNotification: (deviceId: string) => void;
  deleteDevice: (deviceId: string) => void;
  assignDevice: () => void;
  addDevice: () => void;
}
function UserDetailsDevicesTable({
  devices,
  isLoading,
  isPerformingAction,
  resendNotification,
  deleteDevice,
  assignDevice,
  addDevice,
}: UserDetailsDevicesTableProps) {
  const [t] = useTranslation();
  const user = useContext(UserDetailsContext);
  const { userOperations } = useSelector((state: RootState) => state.eSimManagerReducer);
  const [sort, setSort] = useState<Sort>({ order: 'asc', orderBy: 'model' });
  const { isUserAllowed } = useRolePermissions();
  const { routePermissions } = usePortalConfig();
  const { headers, rows, totalDevices } = useUserDetailsDevicesTable(
    devices,
    sort,
    resendNotification,
    isPerformingAction,
    deleteDevice,
  );
  /**
   * Handle change of the table sort
   * @param s
   */
  const handleOnSort = (s: Sort) => {
    setSort(s);
  };

  /**
   * Check if assign device button should be shown
   */
  const showAssignDeviceButton = () =>
    user !== null && userOperations.includes(UserOperationsType.CAN_ASSIGN_DEVICE_TO_USER);

  return (
    <Box paddingBottom="3rem">
      <Box sx={{ display: 'flex', alignItems: 'center', paddingBottom: '1rem' }}>
        <Typography variant="body1">{t('common.table.totalItems', { count: totalDevices })}</Typography>
        <Box sx={{ display: 'flex', marginLeft: 'auto', gap: '1.5rem' }}>
          {isUserAllowed(routePermissions.devicesCreate) ? (
            <Button onClick={addDevice} color="alternative1Button" label={t('pages.userDetails.addDevice')} />
          ) : null}
          {showAssignDeviceButton() ? (
            <Button onClick={assignDevice} label={t('pages.userDetails.assignDevice')} disabled={isPerformingAction} />
          ) : null}
        </Box>
      </Box>
      <Table
        headers={headers}
        rows={rows}
        isLoading={isLoading}
        defaultOrderBy={sort.orderBy}
        onSort={handleOnSort}
        emptyStateMessage={t('pages.userDetails.tabs.devicesTable.noResults')}
      />
    </Box>
  );
}

export default memo(UserDetailsDevicesTable);
